import React from 'react';
import { Empty, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import {
  HistoryOutlined, AppstoreOutlined, ProfileOutlined,
  WalletOutlined, CheckCircleOutlined, ClockCircleOutlined,
  CreditCardOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { PiMoneyWavyBold } from "react-icons/pi";

interface FeeDetailPanelProps {
  record: any;
  entryColumns: any[];
  onDeleteEntry?: (key: string) => void;
  role: string;
}

const money = (n: any) => `₹${Number(n ?? 0).toLocaleString('en-IN')}`;

const MODE_COLOR: Record<string, string> = {
  CASH: 'green', UPI: 'blue', CARD: 'purple',
  NET_BANKING: 'cyan', CHEQUE: 'orange', BANK_TRANSFER: 'geekblue',
};

const FeeDetailPanel: React.FC<FeeDetailPanelProps> = ({ record, onDeleteEntry, role }) => {

  console.log("Role in FeeDetailPanel:", role);

  const entries = record?.feeEntries || [];
  const count = entries.length;
  const total = Number(record?.totalFee ?? 0);
  const paid = Number(record?.paidAmount ?? 0);
  const balance = Number(record?.pendingAmount ?? 0);
  const cleared = balance <= 0;
  const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;

  return (
    <div className="cat-panel">
      <div className="cat-card">
        {/* Header */}
        <div className="cat-card-head">
          <div className="cat-head-left">
            <div className="cat-head-icon fee-head-icon"><PiMoneyWavyBold size={35} /></div>
            <div className="cat-head-titles">
              <div className="cat-head-name">{record?.studentName || 'Student'}</div>
              <div className="cat-head-meta">
                {record?.categoryName && <span className="cat-head-code"><AppstoreOutlined /> {record.categoryName}</span>}
                {record?.serviceName && <span className="cat-head-code"><ProfileOutlined /> {record.serviceName}</span>}
              </div>
            </div>
          </div>
         <div className="fee-stat-grid">
            <div className="fee-stat">
              <span className="fee-stat-label"><WalletOutlined /> Quota Target</span>
              <span className="fee-stat-value">{money(total)}</span>
            </div>
            <div className="fee-stat fee-stat--green">
              <span className="fee-stat-label"><CheckCircleOutlined /> Total Paid</span>
              <span className="fee-stat-value">{money(paid)}</span>
            </div>
            <div className={`fee-stat ${balance > 0 ? 'fee-stat--red' : 'fee-stat--green'}`}>
              <span className="fee-stat-label"><ClockCircleOutlined /> Balance</span>
              <span className="fee-stat-value">{money(balance)}</span>
            </div>
          </div>
        </div>
        <div className="cat-card-body">
         

          {/* Payment history */}
          <div className="fee-history-heading">
            <span className="cat-section-title" style={{ margin: 0 }}><HistoryOutlined /> Payment History</span>
            <span className={`fee-count-badge ${count > 0 ? 'fee-count-badge--active' : 'fee-count-badge--empty'}`}>{count}</span>
          </div>

          {count > 0 ? (
            <div className={`fee-ledger ${onDeleteEntry ? 'fee-ledger--del' : ''}`}>
              {/* Column header */}
              <div className="fee-ledger-head">
                <span className="fee-lg-col fee-lg-col--seq">#</span>
                <span className="fee-lg-col fee-lg-col--date">Date</span>
                <span className="fee-lg-col fee-lg-col--mode">Mode</span>
                <span className="fee-lg-col fee-lg-col--credit">Credit</span>
                <span className="fee-lg-col fee-lg-col--balance">Balance</span>
                <span className="fee-lg-col fee-lg-col--bar">Outstanding</span>
                {onDeleteEntry && <span className="fee-lg-col fee-lg-col--act" />}
              </div>

              {/* Rows */}
              {entries.map((entry: any, idx: number) => {
                const entryBalance = Number(entry?.pendingAmount ?? 0);
                const entryCleared = entryBalance <= 0;
                const mode = entry?.paymentMode;
                const barPct = total > 0 ? Math.min(100, Math.round((entryBalance / total) * 100)) : 0;
                return (
                  <div className="fee-ledger-row" key={entry?.key ?? idx}>
                    <span className="fee-lg-col fee-lg-col--seq">
                      <span className="fee-lg-seq">{idx + 1}</span>
                    </span>
                    <span className="fee-lg-col fee-lg-col--date">
                      <span className="fee-lg-date">
                        {entry?.paymentDate ? dayjs(entry.paymentDate).format('DD MMM YYYY') : '—'}
                      </span>
                    </span>
                    <span className="fee-lg-col fee-lg-col--mode">
                      {mode ? (
                        <Tag color={MODE_COLOR[mode] || 'default'} className="fee-lg-mode">
                          <CreditCardOutlined /> {String(mode).replace(/_/g, ' ')}
                        </Tag>
                      ) : <span className="fee-lg-muted">—</span>}
                    </span>
                    <span className="fee-lg-col fee-lg-col--credit">
                      <span className="fee-lg-credit">+{money(entry?.paidAmount)}</span>
                    </span>
                    <span className="fee-lg-col fee-lg-col--balance">
                      <span className={`fee-lg-balance ${entryCleared ? 'fee-lg-balance--green' : 'fee-lg-balance--red'}`}>
                        {money(entryBalance)}
                        {entryCleared && <CheckCircleOutlined style={{ marginLeft: 4 }} />}
                      </span>
                    </span>
                    <span className="fee-lg-col fee-lg-col--bar">
                      <span className={`fee-lg-bar ${entryCleared ? 'fee-lg-bar--green' : ''}`}>
                        <span className="fee-lg-bar-track">
                          <span className="fee-lg-bar-fill" style={{ width: `${barPct}%` }} />
                        </span>
                        <span className="fee-lg-bar-pct">{entryCleared ? 'Cleared' : `${barPct}%`}</span>
                      </span>
                    </span>
                    {role !== 'STAFF' && onDeleteEntry && (
                      <span className="fee-lg-col fee-lg-col--act">
                        <Tooltip title="Delete payment">
                          <button
                            type="button"
                            className="fee-lg-del"
                            onClick={() => onDeleteEntry(entry.key)}
                          >
                            <DeleteOutlined />
                          </button>
                        </Tooltip>
                      </span>
                    )}
                  </div>
                );
              })}

              {/* Closing balance footer */}
              <div className="fee-ledger-foot">
                <span className="fee-lg-foot-label">Closing balance</span>
                <span className={`fee-lg-foot-value ${cleared ? 'fee-lg-balance--green' : 'fee-lg-balance--red'}`}>
                  {money(balance)}
                </span>
              </div>
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span style={{ color: '#9ca3af', fontSize: 13 }}>No payments recorded yet</span>}
              style={{ padding: '20px 0' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FeeDetailPanel;
